import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getCustomSet, getLessonSets, addLessonToCustomSet, addBulkContentToCustomSet,
  getLesson, CustomSetWithContent, LessonSet, Lesson, Content
} from '../services/api';
import './CustomSetAdd.css';

const CustomSetAdd: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [set, setSet] = useState<CustomSetWithContent | null>(null);
  const [lessonSets, setLessonSets] = useState<LessonSet[]>([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'lessons' | 'words'>('lessons');
  const [selectedLesson, setSelectedLesson] = useState<string | null>(null);
  const [lessonContent, setLessonContent] = useState<Content[]>([]);
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      const [setData, setsData] = await Promise.all([
        getCustomSet(id),
        getLessonSets()
      ]);
      setSet(setData);
      setLessonSets(setsData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddLesson = async (lessonId: string) => {
    if (!id) return;
    setAdding(true);
    try {
      await addLessonToCustomSet(id, lessonId);
      navigate(`/custom-sets/${id}`);
    } catch (err) {
      console.error('Failed to add lesson:', err);
    } finally {
      setAdding(false);
    }
  };

  const handleSelectLesson = async (lessonId: string) => {
    setSelectedLesson(lessonId);
    try {
      const lesson = await getLesson(lessonId);
      setLessonContent(lesson.content || []);
      setSelectedWords(new Set());
    } catch (err) {
      console.error('Failed to load lesson content:', err);
    }
  };

  const toggleWord = (contentId: string) => {
    const newSelected = new Set(selectedWords);
    if (newSelected.has(contentId)) {
      newSelected.delete(contentId);
    } else {
      newSelected.add(contentId);
    }
    setSelectedWords(newSelected);
  };

  const selectAll = () => {
    setSelectedWords(new Set(lessonContent.map(c => c.id)));
  };

  const handleAddSelectedWords = async () => {
    if (!id || selectedWords.size === 0) return;
    setAdding(true);
    try {
      await addBulkContentToCustomSet(id, Array.from(selectedWords));
      navigate(`/custom-sets/${id}`);
    } catch (err) {
      console.error('Failed to add words:', err);
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return <div className="loading">Ładowanie...</div>;
  }

  if (!set) {
    return <div className="error-page">Nie znaleziono zestawu</div>;
  }

  return (
    <div className="custom-set-add fade-in">
      <div className="add-header">
        <button className="btn btn-secondary" onClick={() => navigate(`/custom-sets/${id}`)}>
          ← Wróć
        </button>
        <h1>Dodaj do: {set.name}</h1>
      </div>

      <div className="mode-tabs">
        <button 
          className={`tab ${mode === 'lessons' ? 'active' : ''}`}
          onClick={() => { setMode('lessons'); setSelectedLesson(null); }}
        >
          📚 Całe lekcje
        </button>
        <button 
          className={`tab ${mode === 'words' ? 'active' : ''}`}
          onClick={() => setMode('words')}
        >
          📝 Pojedyncze słówka
        </button>
      </div>

      {mode === 'lessons' && (
        <div className="lessons-browser">
          {lessonSets.map(lessonSet => (
            <div key={lessonSet.id} className="lesson-set-group">
              <h3>{lessonSet.icon} {lessonSet.name_pl}</h3>
              <div className="lessons-list">
                {lessonSet.lessons.map(lesson => (
                  <button
                    key={lesson.id}
                    className="lesson-item-btn"
                    onClick={() => handleAddLesson(lesson.id)}
                    disabled={adding}
                  >
                    <span className="lesson-name">{lesson.name_pl}</span>
                    <span className="lesson-count">
                      {lesson.word_count + lesson.sentence_count} słów
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'words' && !selectedLesson && (
        <div className="lessons-browser">
          <p className="hint">Wybierz lekcję, aby zobaczyć słówka:</p>
          {lessonSets.map(lessonSet => (
            <div key={lessonSet.id} className="lesson-set-group">
              <h3>{lessonSet.icon} {lessonSet.name_pl}</h3>
              <div className="lessons-list">
                {lessonSet.lessons.map(lesson => (
                  <button
                    key={lesson.id}
                    className="lesson-item-btn"
                    onClick={() => handleSelectLesson(lesson.id)}
                  >
                    <span className="lesson-name">{lesson.name_pl}</span>
                    <span className="lesson-count">
                      {lesson.word_count + lesson.sentence_count} słów
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'words' && selectedLesson && (
        <div className="words-selector">
          <div className="words-header">
            <button className="btn btn-secondary btn-small" onClick={() => setSelectedLesson(null)}>
              ← Zmień lekcję
            </button>
            <button className="btn btn-secondary btn-small" onClick={selectAll}>
              Zaznacz wszystkie
            </button>
          </div>

          <div className="words-list">
            {lessonContent.map(content => (
              <label key={content.id} className={`word-item ${selectedWords.has(content.id) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedWords.has(content.id)}
                  onChange={() => toggleWord(content.id)}
                />
                <div className="word-content">
                  <span className="word-english">{content.english}</span>
                  <span className="word-polish">{content.polish}</span>
                </div>
                <span className={`word-type ${content.type}`}>
                  {content.type === 'word' ? '📝' : '💬'}
                </span>
              </label>
            ))}
          </div>

          {selectedWords.size > 0 && (
            <div className="add-words-bar">
              <span>{selectedWords.size} zaznaczonych</span>
              <button
                className="btn btn-primary"
                onClick={handleAddSelectedWords}
                disabled={adding}
              >
                {adding ? 'Dodawanie...' : `Dodaj ${selectedWords.size} słówek`}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSetAdd;

